import React, { FC, useMemo } from 'react';
import { TextButton } from '@blink/components';
import { useSelector } from 'react-redux';

import { useAssetItemStyle } from './AssetItem.style';

import { SelectedBorrowAsset } from '@/features/Modals/ui/BorrowModal';
import { Store } from '@/shared/store';
import { formatWithCommas, trimTrailingZeros } from '@/shared/utils/helpers';
import { AssetWithIcon, Skeleton, SkeletonSize } from '@/shared/ui';

type CollateralAssetCardProps = {
    assetToBorrowName: string;
    amount: string;
    name: string;
    address: string;
    symbol: string;
    decimals: number;
    disabled?: boolean;
    onSelect: (symbol: string) => void;
    selectedInfo?: SelectedBorrowAsset;
    assetSymbol?: string;
};
export const AssetItem: FC<CollateralAssetCardProps> = ({
    amount,
    disabled,
    decimals,
    symbol,
    onSelect,
    assetSymbol,
}) => {
    const classes = useAssetItemStyle();

    const prices = useSelector((state: Store) => state.borrows.userConversionRates);

    const roundedAmount = trimTrailingZeros(amount, decimals);

    const handleOnClick = async () => {
        onSelect(symbol);
    };

    const price = useMemo(() => {
        const itemPrice = prices.find((item) => item.symbol === symbol)?.price;

        if (itemPrice) {
            return itemPrice;
        }

        return 'N/A';
    }, [prices, symbol]);

    const amountInLeveragePrice = useMemo(() => {
        if (price === 'N/A') {
            return 'N/A';
        }

        const result = Number(roundedAmount) * Number(price);

        if (result.toString().includes('e')) {
            return Number(result.toFixed(decimals)).toString();
        }

        return trimTrailingZeros(result.toString(), decimals);
    }, [price, roundedAmount]);

    return (
        <>
            <TextButton
                disabled={disabled || Number(amount) === 0}
                onClick={handleOnClick}
                className={classes.root}
            >
                <AssetWithIcon
                    assetName={symbol || ''}
                    assetSymbol={symbol}
                    className={classes.assetIcon}
                />
                <div className={classes.amountWrapper}>
                    <p className={classes.assetAmount}>
                        {formatWithCommas(roundedAmount)} {symbol}
                    </p>
                    <div>
                        {!price || price === 'N/A' ? (
                            <>
                                <Skeleton size={SkeletonSize.Small} />
                            </>
                        ) : (
                            <>
                                {formatWithCommas(amountInLeveragePrice)} {assetSymbol}
                            </>
                        )}
                    </div>
                </div>
            </TextButton>
        </>
    );
};
